<svelte:options
  customElement={{
    tag: "oc-skip-link-v1",
    /*                       */
    extend: window.__components.extend(),
    props: {
      variant: { type: "String", reflect: true },
    },
  }}
/>

<script lang="ts">
  import { onMount } from "svelte";
  import { focusDeepWithin } from "@otto-ec/otto-components-utils/utils/focus/focusDeepWithin";
  import { focusNextElement } from "./focusNextElement";
  import { getNextParentElement } from "../../../common/utils/getNextParentElement";
  import type { Props } from "./SkipLinkV1.types";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  /*                                                         */
  let { variant }: Props = $props();

  const Host = $host();

  let hasFocus = $state(false);
  const updateHasFocus = (): void => {
    hasFocus = Host.matches(":focus-within");
  };

  let isAnimating = $state(false);

  let isMounted = $state(false); /*                                */
  onMount(() => (isMounted = true));

  const onclick = (ev: MouseEvent): void => {
    let children = ev
      .composedPath()
      .slice(0, ev.composedPath().indexOf(Host)) /*                                              */
      .filter((e): e is HTMLElement => e instanceof HTMLElement);

    const skipToQuery = children.map((c) => c.dataset?.ocSkipLinkV1To).find(Boolean);
    if (skipToQuery) {
      /*                                                      */
      let skipTo = document.body.querySelector<HTMLElement>(skipToQuery);
      if (skipTo) focusDeepWithin(skipTo);
      return;
    }

    const skipAfterQuery = children.map((c) => c.dataset?.ocSkipLinkV1After).find(Boolean);
    if (skipAfterQuery) {
      /*                                                                           */
      let skipAfter = document.body.querySelector<HTMLElement>(skipAfterQuery);
      if (skipAfter) focusNextElement(skipAfter);
      return;
    }

    /*                                                                  */
    let parentElement = getNextParentElement(Host);
    if (parentElement) focusNextElement(parentElement);
  };

  const onslotchange = (ev: Event): void => {
    const slot = ev.currentTarget as HTMLSlotElement;
    slot
      .assignedElements()
      .filter((btn): btn is HTMLOcButtonV1Element => btn.tagName === "OC-BUTTON-V1")
      .forEach((btn) => {
        btn.variant = "secondary";
        btn.size = "50";
        btn.iconTypeLeft = "skip-link";
      });
  };

  /*                                                                 */
  let maxWidth = $derived.by(() => {
    if (!hasFocus) return "100%";
    let left = Number.POSITIVE_INFINITY;
    let right = 0;

    for (const child of Host.children) {
      let rect = child.getBoundingClientRect();
      if (rect.left < left) {
        left = rect.left;
      }
      if (rect.right > right) {
        right = rect.right;
      }
    }

    return `${right - left}px`;
  });
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="skip-link"
  class:--visible={hasFocus}
  class:--animating={isAnimating}
  class:--mounted={isMounted}
  onfocusin={updateHasFocus}
  onfocusout={updateHasFocus}
  ontransitionstart={() => (isAnimating = true)}
  ontransitionend={() => (isAnimating = false)}
  {onclick}
  style:--max-width={maxWidth}
  data-oc-floating-focus-v1-target
>
  <slot {onslotchange} />
</div>

<DefaultStyles />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  :global {
    :host {
      position: absolute;
      margin: tokens.$oc-component-skip-link-position-y
        calc(#{tokens.$oc-component-skip-link-position-x} + var(--oc-skip-link-v1-offset-x, 0px));
      left: 0;
      top: 0;
      z-index: 9999999999; /*                                           */
      pointer-events: none;
      overflow: hidden;

      /*                          */
      filter: tokens.$oc-semantic-shadow-above;
      transform: translateZ(0);
    }

    :host([variant="header"]) {
      position: fixed;
      right: 0;
      justify-items: center;
    }

    .skip-link {
      pointer-events: all;
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      padding: tokens.$oc-component-skip-link-spacing;
      gap: tokens.$oc-component-skip-link-gap;
      border-radius: tokens.$oc-component-skip-link-border-radius;
      background: tokens.$oc-component-skip-link-background-color;
      opacity: 0;

      box-sizing: content-box;
      max-width: var(--max-width);
    }

    .--visible {
      opacity: 1;
    }

    .skip-link:not(.--visible):not(.--animating):not(:focus-within) {
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    /*               */
    @media (prefers-reduced-motion: no-preference) {
      .--mounted {
        transition: opacity tokens.$oc-component-skip-link-disappear-easing
          tokens.$oc-component-skip-link-disappear-duration;
        .--visible {
          transition: opacity tokens.$oc-component-skip-link-appear-easing
            tokens.$oc-component-skip-link-appear-duration;
        }
      }
    }
  }
</style>
